import React, { useCallback, useRef } from 'react';
import { Element, DOMNode } from 'html-react-parser';
import { AllieDownloadIcon } from '../../../styling/icons';
import { DatagridWrapper } from '../FactoryGptResponse/FactoryGptResponse.styles';
import {
  DownloadFormatMenu,
  exportXLSXFileFromRows,
  getTableRows,
  rowsToCsv,
  getExportFilename,
} from '../ReportDownloader';
import { downloadBlobFile } from '../../../common/utils';
import { MarkdownHtmlTable, MarkdownParseOptions } from './markdownHtmlTable';

export type MarkdownTableDownloadProps = {
  onDownloadCsv?: (csvData: string) => void;
  downloadCsvTooltip?: string;
  onDownloadXlsx?: (csvData: string) => void;
};

type ParseOptions = MarkdownParseOptions;

const MarkdownTableWithDownload: React.FC<
  MarkdownTableDownloadProps & { children: React.ReactNode }
> = ({ children, onDownloadCsv, downloadCsvTooltip, onDownloadXlsx }) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleSelectFormat = useCallback(
    async (format: 'csv' | 'xlsx') => {
      const table = ref.current?.querySelector('table');
      if (!table) return;
      const rows = getTableRows(table);
      if (!rows.length) return;

      if (format === 'csv') {
        const csv = rowsToCsv(rows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        await downloadBlobFile(blob, `${getExportFilename()}.csv`);
        onDownloadCsv?.(csv);
        return;
      }

      await exportXLSXFileFromRows(rows, getExportFilename());
      onDownloadXlsx?.(rowsToCsv(rows));
    },
    [onDownloadCsv, onDownloadXlsx],
  );

  return (
    <DatagridWrapper ref={ref}>
      {children}
      <DownloadFormatMenu
        icon={<AllieDownloadIcon />}
        onSelect={handleSelectFormat}
        ariaLabel={downloadCsvTooltip || 'Download'}
      />
    </DatagridWrapper>
  );
};

export function replaceMarkdownTable(
  domNode: DOMNode,
  options: ParseOptions,
  download?: MarkdownTableDownloadProps,
) {
  if (!(domNode instanceof Element) || domNode.name !== 'table') {
    return undefined;
  }

  return (
    <MarkdownTableWithDownload
      onDownloadCsv={download?.onDownloadCsv}
      downloadCsvTooltip={download?.downloadCsvTooltip}
      onDownloadXlsx={download?.onDownloadXlsx}
    >
      <MarkdownHtmlTable tableNode={domNode} options={options} />
    </MarkdownTableWithDownload>
  );
}
